import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { OfferTerms } from "@shared/offerMath";

export type Milestone = "teaser" | "nda" | "cim" | "ioi" | "loi";
export type OfferStage = "ioi" | "loi";

/** Raw-dollar payload sent when marking IOI/LOI or editing terms. */
export interface OfferTermsPayload {
  evLow: number; evHigh: number; ebitda: number;
  rolloverPct: number | null;
  earnoutAmount: number | null;
  earnoutInEv: boolean | null;
  sellerFinancingAmount: number | null;
  notes: string | null;
}

export interface TrackerRow {
  linkId: string; buyerId: string; buyerName: string;
  buyerFirmType: string | null; buyerHQ: string | null;
  teaserSentAt: string | null; ndaExecutedAt: string | null; cimSentAt: string | null;
  ioiReceivedAt: string | null; loiReceivedAt: string | null;
  status: string | null; notes: string | null;
  ioiTerms: OfferTerms | null; loiTerms: OfferTerms | null;
}
export interface TrackerResponse {
  aggregates: { teasers: number; ndas: number; cims: number; iois: number; lois: number; total: number; passed: number };
  ebitda: number | null;
  rows: TrackerRow[]; page: number; pageSize: number; totalRows: number;
}
export interface TrackerQuery {
  page: number; pageSize: number; sort: string; order: "asc" | "desc";
  search: string; status: "all" | "active" | "passed";
  /** Show buyers that have reached ANY of these milestones (empty = all buyers) */
  milestones: Milestone[];
}

export function useBuyerTracker(clientId: string, q: TrackerQuery) {
  const params = new URLSearchParams({
    page: String(q.page), pageSize: String(q.pageSize), sort: q.sort, order: q.order,
    status: q.status, ...(q.search ? { search: q.search } : {}),
    ...(q.milestones.length ? { milestones: q.milestones.join(",") } : {}),
  });
  return useQuery<TrackerResponse>({
    queryKey: ["buyer-tracker", clientId, q],
    queryFn: () => apiRequest("GET", `/api/companies/${clientId}/buyer-tracker?${params.toString()}`).then((r) => r.json()),
    enabled: !!clientId,
  });
}

export function invalidateTracker(clientId: string) {
  queryClient.invalidateQueries({ queryKey: ["buyer-tracker", clientId] });
  queryClient.invalidateQueries({ queryKey: ["offers", clientId] });
  // Terms entry can adopt EBITDA onto the client record
  queryClient.invalidateQueries({ queryKey: ["company", clientId] });
}

export interface OfferRow {
  linkId: string; buyerId: string; buyerName: string;
  buyerFirmType: string | null; buyerHQ: string | null;
  status: string | null; receivedAt: string | null;
  terms: OfferTerms;
}
export interface OffersResponse { ebitda: number | null; ioi: OfferRow[]; loi: OfferRow[] }

export function useOffers(clientId: string, enabled = true) {
  return useQuery<OffersResponse>({
    queryKey: ["offers", clientId],
    queryFn: () => apiRequest("GET", `/api/companies/${clientId}/offers`).then((r) => r.json()),
    enabled: !!clientId && enabled,
  });
}

export interface AddBuyersResult {
  added: number;
  skipped: number;
  /** Link ids for every requested buyer (created, restored, or already present) */
  links: { linkId: string; buyerId: string }[];
}

export function useAddBuyers(clientId: string) {
  return useMutation<AddBuyersResult, Error, { buyerIds: string[]; markTeaserNow: boolean }>({
    mutationFn: (body) =>
      apiRequest("POST", `/api/companies/${clientId}/buyer-tracker/add`, body).then((r) => r.json()),
    onSuccess: () => invalidateTracker(clientId),
  });
}

export type BulkAction =
  | { type: "milestone"; milestone: Milestone; unset?: boolean; date?: string; terms?: OfferTermsPayload }
  | { type: "terms"; stage: OfferStage; terms: OfferTermsPayload }
  | { type: "status"; status: "active" | "passed" }
  | { type: "remove" }
  | { type: "restore" };

export interface BulkActionBody {
  linkIds?: string[];
  selectAllMatching?: boolean;
  filters?: { search?: string; status?: "active" | "passed"; milestones?: Milestone[] };
  action: BulkAction;
}

export interface BulkActionResult {
  affected: number;
  ids: string[];
}

export function useBulkAction(clientId: string) {
  return useMutation<BulkActionResult, Error, BulkActionBody>({
    mutationFn: (body) =>
      apiRequest("POST", `/api/companies/${clientId}/buyer-tracker/bulk`, body).then((r) => r.json()),
    onSuccess: () => invalidateTracker(clientId),
  });
}
